import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";

const STATUS_COLORS = {
  draft: "bg-yellow-50 text-yellow-700",
  published: "bg-green-50 text-green-700",
  private: "bg-blue-50 text-blue-700",
  archived: "bg-zinc-100 text-zinc-500",
  sold: "bg-purple-50 text-purple-700",
};

export default function PropertiesPage() {
  const { adminAxios } = useAdminAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = () => adminAxios.get("/admin/properties").then(({ data }) => setProperties(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await adminAxios.delete(`/admin/properties/${id}`);
    load();
  };

  const filtered = properties.filter(p => {
    const matchSearch = `${p.name_en} ${p.city} ${p.country} ${p.category_name}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-8 min-h-screen bg-zinc-50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Properties</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{properties.length} total properties</p>
        </div>
        <button onClick={() => navigate("/admin/properties/new")} data-testid="add-property-btn" className="flex items-center gap-2 bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-5 py-2.5 transition-colors">
          <Plus size={14} strokeWidth={2} /> Add Property
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search properties..." data-testid="property-search" className="border border-zinc-200 px-4 py-2 text-sm w-64 focus:outline-none focus:border-zinc-400 bg-white" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white">
          <option value="">All Statuses</option>
          {["draft", "published", "private", "archived", "sold"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              {["Property", "Location", "Category", "Type", "Price", "Status", "Views", "Assigned", ""].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-zinc-400">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-zinc-400 text-center">No properties found.</td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.featured_image && <img src={p.featured_image} alt="" className="w-10 h-7 object-cover opacity-90" />}
                    <div>
                      <div className="font-medium text-zinc-900">{p.name_en}</div>
                      {p.internal_ref && <div className="text-[10px] font-mono text-zinc-400">{p.internal_ref}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-600">{[p.city, p.country].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-zinc-600">{p.category_name || "—"}</td>
                <td className="px-4 py-3 text-zinc-500">{p.property_type || "—"}</td>
                <td className="px-4 py-3 text-zinc-600">
                  {p.price_visibility === "hidden" ? "Hidden" : p.price_visibility === "on_request" ? "On Request" : p.price ? `${p.currency} ${Number(p.price).toLocaleString()}` : "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 font-medium capitalize ${STATUS_COLORS[p.status] || "bg-zinc-100 text-zinc-500"}`}>{p.status}</span>
                </td>
                <td className="px-4 py-3 text-zinc-400">{p.view_count || 0}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{(p.assigned_users || []).length} users</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/admin/properties/${p.id}/edit`)} data-testid={`edit-property-${p.id}`} className="text-zinc-400 hover:text-zinc-700 transition-colors"><Edit2 size={13} strokeWidth={1.5} /></button>
                    <button onClick={() => del(p.id, p.name_en)} data-testid={`delete-property-${p.id}`} className="text-zinc-400 hover:text-[#C8102E] transition-colors"><Trash2 size={13} strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
